import type { CountryCode } from "libphonenumber-js";
import parsePhoneNumberFromString from "libphonenumber-js";
import type { Dictionary } from "types";
import { z } from "zod";

import { getZipCodeRegexByMarketCode } from "@/config/market-configurations";
import type { MarketCode } from "@/config/markets/const";

export function getZipCodeOnlySchema({
  marketCode,
  dictionary,
}: {
  marketCode: MarketCode;
  dictionary: Dictionary;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    zipCode: z
      .string()
      .min(1, dictionary.zipCodeRequired ?? "Zip code is required")
      .regex(zipCodeRegex, dictionary.zipCodeInvalid ?? "Invalid zip code"),
  });
}

export function getAddressSchema({
  marketCode,
  dictionary,
}: {
  marketCode: MarketCode;
  dictionary: Dictionary;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    firstName: z
      .string()
      .min(1, dictionary.firstNameRequired ?? "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .string()
      .min(1, dictionary.emailRequired ?? "Email is required")
      .email(dictionary.emailInvalid ?? "Invalid email address"),
    company: z.string().optional(),
    phoneNumber: z
      .string()
      .min(1, dictionary.phoneNumberRequired ?? "Phone number is required"),
    line1: z
      .string()
      .min(1, dictionary.streetRequired ?? "Street address is required"),
    line2: z.string().optional(),
    city: z.string().min(1, dictionary.cityRequired ?? "City is required"),
    zipCode: z
      .string()
      .min(1, dictionary.zipCodeRequired ?? "Zip code is required")
      .regex(zipCodeRegex, dictionary.zipCodeInvalid ?? "Invalid zip code"),
    country: z.string().optional(),
  });
}

export function getDeliveryAddressSchema({
  marketCode,
  dictionary,
}: {
  marketCode: MarketCode;
  dictionary: Dictionary;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    deliveryFirstName: z
      .string()
      .min(1, dictionary.firstNameRequired ?? "First name is required"),
    deliveryLastName: z
      .string()
      .min(1, dictionary.lastNameRequired ?? "Last name is required"),
    deliveryLine1: z
      .string()
      .min(1, dictionary.streetRequired ?? "Street address is required"),
    deliveryCity: z
      .string()
      .min(1, dictionary.cityRequired ?? "City is required"),
    deliveryZipCode: z
      .string()
      .min(1, dictionary.zipCodeRequired ?? "Zip code is required")
      .regex(zipCodeRegex, dictionary.zipCodeInvalid ?? "Invalid zip code"),
    deliveryCountry: z.string().optional(),
  });
}

export const nShiftDeliveryOptionSchema = z.object({
  valid: z.boolean(),
  deliveryCheckoutId: z.string(),
  selectedOptionId: z.string(),
  optionIds: z.array(z.string()),
  price: z.number(),
  agent: z.string().optional(),
  selectedAddons: z.array(z.unknown()),
  fields: z.array(z.unknown()),
  taxRate: z.number(),
});

export function superRefinePhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
  ctx: z.RefinementCtx,
  {
    dictionary,
    marketCode,
  }: { dictionary: Dictionary; marketCode: MarketCode },
) {
  const currentCountryCode = marketCode.toUpperCase() as CountryCode;
  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    currentCountryCode,
  );

  if (!parsed) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message: dictionary.phoneNumberInvalid,
    });
  }

  if (!parsed?.isPossible()) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        dictionary.phoneNumberInvalidLength ??
        "Phone number is too short or too long",
    });
  }

  if (parsed?.country?.includes(marketCode.toUpperCase()) === false) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        dictionary.phoneNumberInvalidCountry ??
        "Invalid country for phone number",
    });
  }

  if (!parsed?.isValid() && parsed?.country) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        dictionary.phoneNumberInvalidCallingCode?.replace(
          "{countryCallingCode}",
          `+${parsed?.countryCallingCode ?? ""}`,
        ) ?? `Number must start with +${parsed?.countryCallingCode ?? ""}`,
    });
  }
}

export function transformPhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
) {
  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    data.country?.toUpperCase() as CountryCode,
  );

  return {
    ...data,
    phoneNumber: parsed?.format("E.164") ?? data.phoneNumber,
  };
}
